// Shared self-media controls used by BOTH the 1:1 chat and the group room, so the two
// behave identically: a mic mute/enable button, a camera toggle/enable button, and a
// settings popover with microphone volume, speaker volume, noise suppression, background
// blur, and a video filter. The speaker target and the local stream differ per screen, so
// they are provided via callbacks (cb.onSpeakerVolume / cb.getLocalStream). ASCII only.

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { getJoinSoundOn, setJoinSoundOn, playJoinTone } from '../tones.js';

const micIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v3"/></svg>';
const micOffIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v3M4 4l16 16"/></svg>';
const camIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="13" height="12" rx="2"/><path d="M22 8l-5 4 5 4V8z"/></svg>';
const camOffIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="13" height="12" rx="2"/><path d="M22 8l-5 4 5 4V8z"/><path d="M3 3l18 18"/></svg>';
const slidersIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>';
const speakerIcon = () =>
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14"/></svg>';

/**
 * @param {{
 *   canVideo?: boolean,
 *   getLocalStream?: () => MediaStream|null,
 *   onEnableMic?: () => void, onEnableCam?: () => void,
 *   onMicVolume?: (gain: number) => void,        // 0..2 -> app mic gain node
 *   onSpeakerVolume?: (vol: number) => void,     // 0..1 -> remote playback (per screen)
 *   onEffect?: (kind: 'denoise'|'blur'|'filter', value: any) => void,
 *   onCamToggle?: (on: boolean) => void,         // self-view visual per screen
 * }} cb
 */
export function createMediaControls(cb = {}) {
  // Build marker: if you do NOT see this in the console when a chat opens, you are running a
  // stale bundle -- rebuild the web (vite build) and hard-refresh.
  try {
    console.info('[lumi] media controls v2 (shared 1:1+group, mic meter, focus blur, audio-resume)');
  } catch {
    /* ignore */
  }
  let micOn = true;
  let camOn = true;
  let hasAudio = false;
  let hasVideo = false;
  const canVideo = cb.canVideo !== false;
  const getStream = () => (cb.getLocalStream ? cb.getLocalStream() : null);

  const micBtn = el('button', {
    class: 'btn btn-icon btn-ghost media-toggle btn-off',
    title: t('Turn on microphone'),
    'aria-label': 'Turn on microphone',
    html: micOffIcon(),
    onClick: () => {
      if (!hasAudio) {
        cb.onEnableMic?.();
        return;
      }
      const s = getStream();
      if (!s) return;
      micOn = !micOn;
      s.getAudioTracks().forEach((track) => { track.enabled = micOn; });
      updateMicBtn();
    },
  });
  const camBtn = el('button', {
    class: 'btn btn-icon btn-ghost media-toggle btn-off',
    title: t('Turn on camera'),
    'aria-label': 'Turn on camera',
    html: camOffIcon(),
    onClick: () => {
      if (!hasVideo) {
        cb.onEnableCam?.();
        return;
      }
      const s = getStream();
      if (!s) return;
      camOn = !camOn;
      s.getVideoTracks().forEach((track) => { track.enabled = camOn; });
      cb.onCamToggle?.(camOn);
      updateCamBtn();
    },
  });

  function updateMicBtn() {
    if (!hasAudio) {
      micBtn.innerHTML = micOffIcon();
      micBtn.classList.add('btn-off');
      micBtn.title = 'Turn on microphone';
    } else {
      micBtn.innerHTML = micOn ? micIcon() : micOffIcon();
      micBtn.classList.toggle('btn-off', !micOn);
      micBtn.title = micOn ? 'Mute microphone' : 'Unmute microphone';
    }
    micBtn.setAttribute('aria-label', micBtn.title);
    micBtn.setAttribute('aria-pressed', String(hasAudio && !micOn));
  }
  function updateCamBtn() {
    camBtn.classList.toggle('hidden', !canVideo);
    if (!hasVideo) {
      camBtn.innerHTML = camOffIcon();
      camBtn.classList.add('btn-off');
      camBtn.title = 'Turn on camera';
    } else {
      camBtn.innerHTML = camOn ? camIcon() : camOffIcon();
      camBtn.classList.toggle('btn-off', !camOn);
      camBtn.title = camOn ? 'Turn camera off' : 'Turn camera on';
    }
    camBtn.setAttribute('aria-label', camBtn.title);
    camBtn.setAttribute('aria-pressed', String(hasVideo && !camOn));
  }

  // Volume: speaker = remote playback level (per screen); microphone = gain on sent audio.
  const speakerRange = el('input', { type: 'range', min: '0', max: '100', value: '100', class: 'vol-range', 'aria-label': 'Speaker volume' });
  speakerRange.addEventListener('input', () => cb.onSpeakerVolume?.(Number(speakerRange.value) / 100));
  // On release, play a short chime at the chosen level. The speaker slider governs the OTHER
  // person's live audio, which you cannot hear when testing alone -- the chime makes it
  // audible and adjustable solo (and works on iOS, where media-element volume is read-only).
  speakerRange.addEventListener('change', () => playSpeakerTone(Number(speakerRange.value) / 100));
  const micRange = el('input', { type: 'range', min: '0', max: '200', value: '100', class: 'vol-range', 'aria-label': 'Microphone volume' });
  micRange.addEventListener('input', () => {
    const g = Number(micRange.value) / 100;
    cb.onMicVolume?.(g); // gain on the audio actually sent to the other side
    if (meterGain) meterGain.gain.value = g; // ...and the meter shows that post-gain level move
  });
  // Live mic level meter: the microphone volume changes what OTHERS hear (not your own
  // playback), so without this it feels dead. The bar shows your input level scaled by the
  // current volume, so dragging the slider visibly moves it.
  const micMeterFill = el('div', { class: 'mic-meter-fill' });
  const micMeter = el('div', { class: 'mic-meter', title: t('Your microphone level (scaled by the volume slider)') }, [micMeterFill]);
  let meterCtx = null;
  let meterAnalyser = null;
  let meterData = null;
  let meterRAF = 0;
  let meterGain = null; // mic-slider gain feeding the meter, so the meter shows the SENT level
  function tickMeter() {
    if (!meterAnalyser || settingsPanel.classList.contains('hidden')) {
      meterRAF = 0;
      return;
    }
    meterAnalyser.getByteTimeDomainData(meterData);
    let sum = 0;
    for (let i = 0; i < meterData.length; i += 1) {
      const x = (meterData[i] - 128) / 128;
      sum += x * x;
    }
    const rms = Math.sqrt(sum / meterData.length);
    // rms is already post-gain (meterGain). 2.5x display scale keeps a normal voice mid-bar
    // so moving the slider visibly moves the meter across its whole range (no early clipping).
    const level = Math.min(1, rms * 2.5);
    micMeterFill.style.width = `${Math.round(level * 100)}%`;
    meterRAF = requestAnimationFrame(tickMeter);
  }
  function startMeter() {
    if (!meterRAF && meterAnalyser) meterRAF = requestAnimationFrame(tickMeter);
  }
  // A short, gentle chime at the given speaker level (0..1), for solo speaker-volume testing.
  function playSpeakerTone(vol) {
    try {
      if (!meterCtx) meterCtx = new (window.AudioContext || window.webkitAudioContext)();
      meterCtx.resume?.();
      const startAt = meterCtx.currentTime;
      const osc = meterCtx.createOscillator();
      const g = meterCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 523.25; // soft C5
      const peak = Math.max(0, Math.min(1, vol)) * 0.5; // never harsh, even at full
      g.gain.setValueAtTime(0, startAt);
      g.gain.linearRampToValueAtTime(peak, startAt + 0.02);
      g.gain.linearRampToValueAtTime(0, startAt + 0.22);
      osc.connect(g).connect(meterCtx.destination);
      osc.start(startAt);
      osc.stop(startAt + 0.24);
    } catch {
      /* ignore -- no Web Audio */
    }
  }
  // Effects (client-side, applied to your own stream before it is sent).
  const denoiseCheck = el('input', { type: 'checkbox', onChange: (e) => cb.onEffect?.('denoise', e.target.checked) });
  const blurCheck = el('input', { type: 'checkbox', onChange: (e) => cb.onEffect?.('blur', e.target.checked) });
  const filterSelect = el('select', { class: 'fx-filter', 'aria-label': 'Video filter', onChange: (e) => cb.onEffect?.('filter', e.target.value) }, [
    el('option', { value: 'none', text: t('No filter') }),
    el('option', { value: 'mono', text: t('Mono') }),
    el('option', { value: 'warm', text: t('Warm') }),
    el('option', { value: 'cool', text: t('Cool') }),
    el('option', { value: 'dream', text: t('Dream') }),
  ]);
  // The one sound switch, muteable right where the other audio controls are --
  // someone who finds them irritating should not have to hunt for it. It
  // governs all four tones: somebody joining, a notice, a call ringing, and a
  // message arriving while you are looking at another tab. The label used to
  // say "Sound when someone joins", which was true when that was the only tone
  // and became a lie the moment it was not.
  const joinSoundCheck = el('input', {
    type: 'checkbox',
    onChange: (e) => {
      setJoinSoundOn(e.target.checked);
      if (e.target.checked) playJoinTone(); // hear what you just switched on
    },
  });
  joinSoundCheck.checked = getJoinSoundOn();

  const settingsPanel = el('div', { class: 'settings-panel hidden' }, [
    el('div', { class: 'mic-control' }, [
      el('label', { class: 'vol-row' }, [el('span', { html: micIcon() }), micRange]),
      micMeter,
    ]),
    el('label', { class: 'vol-row' }, [el('span', { html: speakerIcon() }), speakerRange]),
    el('div', { class: 'fx-divider' }),
    el('label', { class: 'fx-row' }, [denoiseCheck, el('span', { text: t('Noise suppression') })]),
    el('label', { class: 'fx-row' }, [blurCheck, el('span', { text: t('Background blur') })]),
    el('label', { class: 'fx-row' }, [el('span', { text: t('Filter') }), filterSelect]),
    el('label', { class: 'fx-row' }, [
      joinSoundCheck,
      el('span', {
        text: t('Sounds'),
        title: t('A short tone when someone joins a room, when a regular is around, when a call comes in, and when a message arrives while you are looking elsewhere.'),
      }),
    ]),
  ]);
  const settingsBtn = el('button', {
    class: 'btn btn-icon btn-ghost media-toggle hidden',
    title: t('Settings'),
    'aria-label': 'Audio, video, and effects settings',
    html: slidersIcon(),
    onClick: () => {
      settingsPanel.classList.toggle('hidden');
      if (!settingsPanel.classList.contains('hidden')) {
        meterCtx?.resume?.().catch(() => {}); // a click is a gesture -> wake a suspended meter
        startMeter();
      }
    },
  });
  // Collapse the popover on an outside click (its own button still toggles it).
  document.addEventListener('mousedown', (e) => {
    if (!settingsPanel.isConnected || settingsPanel.classList.contains('hidden')) return;
    if (settingsPanel.contains(e.target) || settingsBtn.contains(e.target)) return;
    settingsPanel.classList.add('hidden');
  });

  updateMicBtn();
  updateCamBtn();

  return {
    micBtn,
    camBtn,
    settingsBtn,
    settingsPanel,
    api: {
      /** Reflect the local stream's tracks and reveal the settings button. */
      setLocalStream(stream) {
        micOn = true;
        camOn = true;
        hasAudio = !!stream && stream.getAudioTracks().length > 0;
        hasVideo = !!stream && stream.getVideoTracks().length > 0;
        updateMicBtn();
        updateCamBtn();
        settingsBtn.classList.toggle('hidden', !(hasAudio || hasVideo));
        settingsPanel.classList.add('hidden');
        micRange.value = '100'; // the mic gain node is rebuilt with the stream
        // Do NOT reset the speaker: this runs again mid-chat (turning a camera
        // or mic on), and snapping playback back to 100% silently undid the
        // level the user had chosen. Re-apply their setting to the new stream.
        cb.onSpeakerVolume?.(Number(speakerRange.value) / 100);
        // (Re)build the level-meter analyser from the live mic track.
        try {
          const audio = hasAudio ? stream.getAudioTracks() : [];
          if (audio.length) {
            if (!meterCtx) meterCtx = new (window.AudioContext || window.webkitAudioContext)();
            const srcNode = meterCtx.createMediaStreamSource(new MediaStream([audio[0]]));
            meterGain = meterCtx.createGain();
            meterGain.gain.value = Number(micRange.value) / 100; // start at the slider's value
            meterAnalyser = meterCtx.createAnalyser();
            meterAnalyser.fftSize = 256;
            meterData = new Uint8Array(meterAnalyser.frequencyBinCount);
            srcNode.connect(meterGain).connect(meterAnalyser); // meter = mic AFTER the slider gain
            meterCtx.resume?.();
          } else {
            meterAnalyser = null;
          }
        } catch {
          meterAnalyser = null; // no Web Audio -> the slider still works, just no meter
        }
      },
      /** Sync the effect controls to saved preferences WITHOUT firing onEffect. */
      initEffects({ denoise, blur, filter } = {}) {
        denoiseCheck.checked = !!denoise;
        blurCheck.checked = !!blur;
        if (filter) filterSelect.value = filter;
      },
    },
  };
}

export default { createMediaControls };
